<template>
  <base-modal
    has-close-btn
    :heading="
      isSubscribed ? $t('unfollow_collection') : $t('follow_collection')
    "
    :show="show"
    size="md"
    @close:modal="closeModal"
  >
    <div class="tw-flex tw-space-x-4">
      <img
        class="tw-w-36 tw-h-20 mod-global--border-radius"
        :class="{ 'tw-filter tw-blur': blurImage }"
        :src="collection.logo[CARD_IMAGE_KEY]"
        alt="Collection logo"
      />
      <div class="tw-flex tw-flex-col tw-space-y-2">
        <h6 class="tw-text-h6 tw-font-medium">
          {{ collection.name }}
        </h6>
        <div
          class="tw-flex tw-text-xs tw-font-medium tw-opacity-70 tw-space-x-2"
        >
          <p>
            {{ collection.stats?.mods_total || 0 }}
            {{ ugcName(game, collection.stats?.mods_total < 2, true).value }}
          </p>
          <p>•</p>
          <p>{{ humanFilesize(collection.filesize) }}</p>
        </div>
      </div>
    </div>

    <inline-message
      v-if="collection.incomplete"
      class="tw-mt-8"
      warning
      no-bg
      extra-small
      :has-close="false"
      :text="
        $t('collections_incomplete', { ugc: ugcName(game, false, true).value })
      "
    />

    <base-divider class="tw-mt-6 tw-mb-4" />

    <base-checkbox
      alt-bg
      :background="false"
      :error-gap="false"
      :label="
        $t(isSubscribed ? 'collection_unsub_mods' : 'collection_sub_mods', {
          ugc: ugcName(game, false, true).value,
        })
      "
      :hint="
        isSubscribed
          ? $t('collection_unsub_hint', {
              ugc: ugcName(game, false, true).value,
            })
          : ''
      "
      :model-value="isSubscribed ? collectionUnSubscribe : collectionSubscribe"
      @input="
        (val) =>
          isSubscribed
            ? (collectionUnSubscribe = !!val)
            : (collectionSubscribe = !!val)
      "
    />
    <template #footer>
      <base-button
        primary
        full-width-at="xs"
        :status="status"
        @click="toggleFollow()"
      >
        {{ isSubscribed ? $t('unfollow') : $t('follow') }}
      </base-button>
    </template>
  </base-modal>
</template>

<script>
import { collectionFollowingStore, subscriptionStore, gameStore } from '@stores'
import InlineMessage from '@components/Message/InlineMessage.vue'
import { GAME_ADULTS_ONLY } from '@config/options.js'
import { humanFilesize } from '@helpers/utils.js'
import { computed, toRefs, ref } from 'vue'
import { CARD_IMAGE_KEY } from '@config'
import { useI18n } from 'vue-i18n'
import {
  unsubscribeCollectionRequest,
  subscribeCollectionRequest,
  getMySubscriptionsRequest,
  unfollowCollectionRequest,
  followCollectionRequest,
} from '@services'
import {
  useResourceValue,
  usePermissions,
  useStatus,
  useAsync,
  addToast,
} from '@composables'

export default {
  components: {
    InlineMessage,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      required: true,
    },
    isSubscribed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['confirm:archive', 'close:modal'],
  setup(props, { emit }) {
    const { collection, isSubscribed } = toRefs(props)
    const { ugcName } = useResourceValue()
    const { t } = useI18n()
    const { getPublicGame } = gameStore()
    const game = getPublicGame()

    const collectionUnSubscribe = ref(false)
    const collectionSubscribe = ref(true)

    const { hasPermissionForCollection } = usePermissions()
    const { setMySubscriptions } = subscriptionStore()
    const { removeCollectionFollow, addCollectionFollow } =
      collectionFollowingStore()

    const {
      loading: loadingSubscribeCollection,
      error: subscribeCollectionError,
      run: runSubscribeCollection,
    } = useAsync(
      (collectionNameId, collectionId) =>
        subscribeCollectionRequest(collectionNameId, collectionId),
      t('collection_error_subscribe', { ugc: ugcName(game, false, true).value })
    )

    const {
      loading: loadingUnsubscribeCollection,
      error: unSubscribeCollectionError,
      run: runUnSubscribeCollection,
    } = useAsync(
      (collectionNameId, collectionId) =>
        unsubscribeCollectionRequest(collectionNameId, collectionId),
      t('collection_error_unsubscribe', {
        ugc: ugcName(game, false, true).value,
      })
    )

    const {
      errorRef: errorRefFollow,
      error: errorFollow,
      run: runFollow,
    } = useAsync((collectionNameId, collectionId) =>
      followCollectionRequest(collectionNameId, collectionId)
    )

    const {
      errorRef: errorRefUnfollow,
      error: errorUnfollow,
      run: runUnfollow,
    } = useAsync((collectionNameId, collectionId) =>
      unfollowCollectionRequest(collectionNameId, collectionId)
    )

    const {
      error: errorGetSubscriptions,
      data: dataGetSubscriptions,
      run: runGetSubscriptions,
    } = useAsync(() =>
      getMySubscriptionsRequest({ game_id: collection.value.game_id })
    )

    const { status } = useStatus({
      loading: computed(
        () =>
          loadingUnsubscribeCollection.value || loadingSubscribeCollection.value
      ),
      error: computed(
        () => unSubscribeCollectionError.value || subscribeCollectionError.value
      ),
    })

    const adultContent = computed(
      () =>
        collection.value.maturity_option > 0 ||
        game.value.maturity_options === GAME_ADULTS_ONLY
    )

    const blurImage = computed(() => {
      const hasAccess = hasPermissionForCollection(
        collection.value.name_id,
        game.value.name_id
      )

      return adultContent.value && !hasAccess
    })

    async function toggleFollow() {
      const follow = !isSubscribed.value
      const subscribeToMods = follow && collectionSubscribe.value
      const unsubscribeFromMods = !follow && collectionUnSubscribe.value

      if (!subscribeToMods && !unsubscribeFromMods) {
        closeModal()

        follow
          ? addCollectionFollow(collection.value.id, collection.value)
          : removeCollectionFollow(collection.value.id)
      }

      const requests = [
        follow
          ? runFollow(collection.value.name_id, collection.value.id)
          : runUnfollow(collection.value.name_id, collection.value.id),
      ]

      if (subscribeToMods) {
        requests.push(
          runSubscribeCollection(collection.value.name_id, collection.value.id)
        )
      } else if (unsubscribeFromMods) {
        requests.push(
          runUnSubscribeCollection(
            collection.value.name_id,
            collection.value.id
          )
        )
      }

      await Promise.all(requests)

      if (follow ? errorFollow.value : errorUnfollow.value) {
        // Revert action
        follow
          ? removeCollectionFollow(collection.value.id)
          : addCollectionFollow(collection.value.id, collection)

        addToast({
          title: t(
            follow ? 'collection_error_follow' : 'collection_error_unfollow'
          ),
          isError: true,
          errorRef: follow ? errorRefFollow.value : errorRefUnfollow.value,
          text: follow ? errorFollow.value : errorUnfollow.value,
        })
      }

      if (
        (subscribeToMods && !subscribeCollectionError.value) ||
        (unsubscribeFromMods && !unSubscribeCollectionError.value)
      ) {
        refreshSubscriptions()
      }

      if (subscribeToMods || unsubscribeFromMods) {
        closeModal()

        follow
          ? addCollectionFollow(collection.value.id, collection.value)
          : removeCollectionFollow(collection.value.id)
      }
    }

    async function refreshSubscriptions() {
      await runGetSubscriptions()

      if (!errorGetSubscriptions.value) {
        setMySubscriptions(dataGetSubscriptions.value.data)
      }
    }

    function closeModal() {
      emit('close:modal')
      collectionUnSubscribe.value = false
      collectionSubscribe.value = true
    }

    return {
      collectionUnSubscribe,
      collectionSubscribe,
      CARD_IMAGE_KEY,
      humanFilesize,
      toggleFollow,
      closeModal,
      blurImage,
      ugcName,
      status,
      game,
    }
  },
}
</script>
